import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { BrainCircuit, X, ChevronRight, CheckCircle2, XCircle, RotateCcw } from "lucide-react";

const TOPICS = [
  { id: "computer_basics", label: "1. Computer Basics", emoji: "💻" },
  { id: "ms_office",       label: "2. MS Office",       emoji: "📄" },
  { id: "networks",        label: "3. Networks",         emoji: "🌐" },
  { id: "internet",        label: "4. Internet",         emoji: "🔗" },
  { id: "ai_basics",       label: "5. AI Basics",        emoji: "🤖" },
];

export default function AITestAgent() {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("welcome");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [inputVal, setInputVal] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [phase, questions.length]);

  const reset = () => {
    setPhase("welcome"); setEmail(""); setMobile(""); setSelectedTopic(null);
    setQuestions([]); setAnswers({}); setResult(null); setInputVal(""); setError("");
  };

  const handleEmailSubmit = () => {
    const val = inputVal.trim();
    if (!val || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      setError("Please enter a valid email address"); return;
    }
    setEmail(val); setInputVal(""); setError(""); setPhase("mobile");
  };

  const handleMobileSubmit = () => {
    const val = inputVal.trim().replace(/\s/g, "");
    if (!val || !/^[0-9+]{10,15}$/.test(val)) {
      setError("Please enter a valid mobile number (10–15 digits)"); return;
    }
    setMobile(val); setInputVal(""); setError(""); setPhase("topic");
  };

  const handleTopicSelect = async (topic) => {
    setSelectedTopic(topic); setLoading(true); setError("");
    try {
      const { data } = await api.get(`/test/questions/${topic.id}`);
      setQuestions(data.questions); setAnswers({}); setPhase("quiz");
    } catch {
      setError("Failed to load questions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const answered = Object.keys(answers).length;
    if (answered < questions.length) {
      setError(`Please answer all questions (${answered}/${questions.length} answered)`); return;
    }
    setLoading(true); setError(""); setPhase("submitting");
    try {
      const answerList = questions.map((q) => ({ question_id: q.id, selected: answers[q.id] }));
      const { data } = await api.post("/test/submit", { email, mobile, topic: selectedTopic.id, answers: answerList });
      setResult(data); setPhase("result");
    } catch {
      setError("Failed to submit. Please try again."); setPhase("quiz");
    } finally {
      setLoading(false);
    }
  };

  const answeredCount = Object.keys(answers).length;
  const allAnswered = questions.length > 0 && answeredCount === questions.length;

  return (
    <>
      {/* Floating button — bottom-left */}
      <div className="fixed bottom-20 left-5 z-[90]">
        <button
          onClick={() => setOpen(!open)}
          className="relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center bg-[#7C3AED] hover:bg-[#6D28D9] text-white transition-all"
          aria-label="AI Test Agent"
          data-testid="test-agent-toggle"
        >
          <BrainCircuit className="w-6 h-6" />
          {!open && (
            <span className="absolute top-0 right-0 w-3 h-3 bg-amber-400 rounded-full border-2 border-white animate-pulse" />
          )}
        </button>
        {!open && (
          <div className="hidden md:block absolute left-16 bottom-1.5 bg-white text-[#7C3AED] text-xs font-semibold px-3 py-2 rounded-lg shadow border border-border whitespace-nowrap pointer-events-none">
            Test Yourself!
            <span className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-l border-b border-border rotate-45" />
          </div>
        )}
      </div>

      {/* Panel */}
      {open && (
        <div
          className="fixed inset-0 z-[100] sm:inset-auto sm:bottom-20 sm:left-5 sm:w-[430px] sm:h-[680px] sm:max-h-[88vh] flex flex-col bg-white sm:rounded-2xl shadow-2xl border border-border overflow-hidden"
          data-testid="test-agent-panel"
        >
          {/* Header */}
          <div className="bg-[#7C3AED] text-white p-4 flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center shrink-0">
              <BrainCircuit className="w-5 h-5 text-[#7C3AED]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm tracking-wide">AI TEST AGENT</div>
              <div className="text-[11px] text-purple-200 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                Test your knowledge • 15 MCQs per topic
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-white/10" data-testid="test-agent-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto bg-[#F9F8F6] p-4 space-y-4">

            {/* Welcome */}
            {phase === "welcome" && (
              <>
                <AgentBubble>
                  👋 Hello! I'm <strong>AI TEST AGENT</strong> — your personal knowledge tester!<br /><br />
                  I'll give you <strong>15 MCQ questions</strong> on your chosen topic and instantly evaluate your score.<br /><br />
                  <strong>Available topics:</strong><br />
                  💻 Computer Basics &nbsp;|&nbsp; 📄 MS Office<br />
                  🌐 Networks &nbsp;|&nbsp; 🔗 Internet &nbsp;|&nbsp; 🤖 AI Basics
                </AgentBubble>
                <AgentBubble>
                  Let's start! I'll need your <strong>email address</strong> to save your results. 📧
                </AgentBubble>
                <div className="flex justify-end">
                  <button
                    onClick={() => setPhase("email")}
                    className="inline-flex items-center gap-2 bg-[#7C3AED] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#6D28D9] transition-colors"
                    data-testid="start-test-btn"
                  >
                    Start Test <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}

            {/* Email prompt */}
            {phase === "email" && (
              <AgentBubble>Please enter your <strong>email address</strong> below 📧</AgentBubble>
            )}

            {/* Mobile prompt */}
            {phase === "mobile" && (
              <>
                <AgentBubble>Got it! ✅</AgentBubble>
                <UserBubble>{email}</UserBubble>
                <AgentBubble>Now please enter your <strong>mobile number</strong> 📱</AgentBubble>
              </>
            )}

            {/* Topic selection */}
            {phase === "topic" && (
              <>
                <AgentBubble>
                  Registered with:<br />
                  📧 <strong>{email}</strong><br />
                  📱 <strong>{mobile}</strong>
                </AgentBubble>
                <AgentBubble>Choose your <strong>test topic</strong> below 👇</AgentBubble>
                <div className="space-y-2">
                  {TOPICS.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => handleTopicSelect(t)}
                      disabled={loading}
                      className="w-full flex items-center gap-3 bg-white border-2 border-[#7C3AED]/30 hover:border-[#7C3AED] hover:bg-[#7C3AED]/5 text-left px-4 py-3 rounded-xl transition-colors text-sm font-medium text-[#1a1a2e] disabled:opacity-50"
                      data-testid={`topic-btn-${t.id}`}
                    >
                      <span className="text-xl">{t.emoji}</span> {t.label}
                    </button>
                  ))}
                </div>
                {loading && <p className="text-center text-sm text-[#7C3AED] animate-pulse">Loading questions…</p>}
                {error && <ErrBox>{error}</ErrBox>}
              </>
            )}

            {/* Quiz */}
            {(phase === "quiz" || phase === "submitting") && (
              <>
                <AgentBubble>
                  Topic: <strong>{selectedTopic?.label}</strong><br />
                  Answer all <strong>{questions.length} questions</strong> then hit Submit! 🎯
                </AgentBubble>

                {questions.map((q, idx) => (
                  <div key={q.id} className="bg-white rounded-xl border border-border p-4 shadow-sm">
                    <p className="text-sm font-semibold text-[#1a1a2e] mb-3">
                      <span className="text-[#7C3AED] mr-1">Q{idx + 1}.</span>{q.question}
                    </p>
                    <div className="space-y-2">
                      {q.options.map((opt, oi) => (
                        <label
                          key={oi}
                          className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer transition-colors border ${
                            answers[q.id] === oi
                              ? "bg-[#7C3AED]/10 border-[#7C3AED]"
                              : "hover:bg-gray-50 border-transparent"
                          }`}
                        >
                          <input
                            type="radio"
                            name={`q-${q.id}`}
                            value={oi}
                            checked={answers[q.id] === oi}
                            onChange={() => setAnswers({ ...answers, [q.id]: oi })}
                            className="mt-0.5 accent-[#7C3AED]"
                          />
                          <span className="text-sm text-[#1a1a2e]">
                            <span className="font-semibold text-[#7C3AED] mr-1">{String.fromCharCode(65 + oi)}.</span>
                            {opt}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                {error && <ErrBox>{error}</ErrBox>}

                <div className="sticky bottom-0 bg-[#F9F8F6] pt-2 pb-1">
                  <p className="text-xs text-center text-[#5C6661] mb-2">
                    {answeredCount}/{questions.length} answered
                  </p>
                  <button
                    onClick={handleSubmit}
                    disabled={!allAnswered || phase === "submitting"}
                    className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-50 text-white py-3 rounded-xl text-sm font-semibold transition-colors"
                    data-testid="submit-test-btn"
                  >
                    {phase === "submitting" ? "Evaluating…" : "Submit Answers"}
                  </button>
                </div>
              </>
            )}

            {/* Result */}
            {phase === "result" && result && (
              <>
                <AgentBubble>
                  🎉 Test complete! Results for <strong>{selectedTopic?.label}</strong>:
                </AgentBubble>

                <div className="bg-white rounded-2xl border-2 border-[#7C3AED] p-5 text-center">
                  <div className={`text-5xl font-bold mb-1 ${result.percentage >= 60 ? "text-green-600" : "text-red-500"}`}>
                    {result.score}/{result.total}
                  </div>
                  <div className={`text-2xl font-semibold mb-2 ${result.percentage >= 60 ? "text-green-600" : "text-red-500"}`}>
                    {result.percentage}%
                  </div>
                  <div className="text-sm text-[#5C6661]">
                    {result.percentage >= 80 ? "Excellent! 🌟" : result.percentage >= 60 ? "Good job! 👍" : "Keep practicing! 💪"}
                  </div>
                  <div className="flex justify-center gap-6 mt-4 text-sm">
                    <span className="flex items-center gap-1 text-green-600 font-semibold">
                      <CheckCircle2 className="w-4 h-4" />
                      {result.details.filter((d) => d.is_correct).length} Correct
                    </span>
                    <span className="flex items-center gap-1 text-red-500 font-semibold">
                      <XCircle className="w-4 h-4" />
                      {result.details.filter((d) => !d.is_correct).length} Wrong
                    </span>
                  </div>
                </div>

                <details className="bg-white rounded-xl border border-border overflow-hidden">
                  <summary className="px-4 py-3 text-sm font-semibold text-[#7C3AED] cursor-pointer hover:bg-purple-50 select-none">
                    View Answer Breakdown
                  </summary>
                  <div className="divide-y divide-border max-h-64 overflow-y-auto">
                    {result.details.map((d, i) => (
                      <div key={i} className={`px-4 py-3 ${d.is_correct ? "bg-green-50" : "bg-red-50"}`}>
                        <p className="text-xs font-semibold text-[#1a1a2e] mb-1">
                          {d.is_correct ? "✅" : "❌"} Q{i + 1}. {d.question}
                        </p>
                        <p className="text-xs text-[#5C6661]">
                          Your answer:{" "}
                          <span className={d.is_correct ? "text-green-700 font-semibold" : "text-red-600 font-semibold"}>
                            {String.fromCharCode(65 + d.selected)}. {d.options[d.selected]}
                          </span>
                          {!d.is_correct && (
                            <span className="text-green-700 ml-2">
                              ✓ Correct: <strong>{String.fromCharCode(65 + d.correct)}. {d.options[d.correct]}</strong>
                            </span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                </details>

                <button
                  onClick={reset}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#7C3AED] text-white py-3 rounded-xl text-sm font-semibold hover:bg-[#6D28D9] transition-colors"
                  data-testid="try-again-btn"
                >
                  <RotateCcw className="w-4 h-4" /> Try Another Topic
                </button>
              </>
            )}
          </div>

          {/* Input footer for email / mobile phases */}
          {(phase === "email" || phase === "mobile") && (
            <div className="p-3 border-t border-border bg-white shrink-0">
              {error && <p className="text-red-500 text-xs mb-2 px-1">{error}</p>}
              <div className="flex gap-2">
                <input
                  type={phase === "email" ? "email" : "tel"}
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") phase === "email" ? handleEmailSubmit() : handleMobileSubmit();
                  }}
                  placeholder={phase === "email" ? "your@email.com" : "Mobile number"}
                  className="flex-1 border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#7C3AED]"
                  data-testid={`test-${phase}-input`}
                  autoFocus
                />
                <button
                  onClick={phase === "email" ? handleEmailSubmit : handleMobileSubmit}
                  className="w-10 h-10 rounded-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white flex items-center justify-center shrink-0 transition-colors"
                  data-testid={`test-${phase}-submit`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function AgentBubble({ children }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[92%] bg-white border border-border rounded-2xl rounded-bl-md px-4 py-3 text-sm text-[#222B26] leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function UserBubble({ children }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] bg-[#7C3AED] text-white rounded-2xl rounded-br-md px-4 py-3 text-sm">
        {children}
      </div>
    </div>
  );
}

function ErrBox({ children }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{children}</div>
  );
}
